// Rapporten — report catalog (FRONTEND-BLUEPRINT §3.6). A single-page catalog of the 10 report types,
// each a shadcn Card with its parameters + a "Rapport opvragen" button. Reports backed by an existing
// export method are wired (RoPA Art.30 → exportRopa CSV/XML); the rest are marked "binnenkort" until
// their backend lands (frontend-only; no new endpoints). PLAN — round-2 fan-out.
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FileBarChart, Download } from "lucide-react";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Can } from "../../lib/rbac";
import { useSession } from "../../lib/session";
import { type PermissionCode } from "@sympllex/auth";
import { PageHeader } from "../../components/page-header";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const errText = (e: unknown): string =>
  e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message;

// trigger a browser download for a text/blob artefact.
function downloadBlob(content: BlobPart, type: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ReportsRoute() {
  const { t } = useI18n();
  return (
    <section>
      <PageHeader title={t("page.reports")} description={t("page.reports.sub")} help={t("help.reports")} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <RegisterVerwerkingenCard />
        <ComplexityReport />
        <ActivityPlanningReport tKey="2" mine={false} />
        <ActivityPlanningReport tKey="3" mine={true} />
        <RegisterBestandssystemenCard />
        <VerantwoordingsstatusCard />
        <TicketReport />
        <PrivacyteamReport />
        <VerwerkersReport />
        <PrivacyStatementReport />
      </div>
    </section>
  );
}

/** Shared card chrome for a single report template. */
function ReportCard({
  tKey,
  children,
  footer,
}: {
  tKey: string;
  children?: React.ReactNode;
  footer: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileBarChart className="size-4 text-primary" aria-hidden />
          {t(`rep.${tKey}.title`)}
        </CardTitle>
        <CardDescription>{t(`rep.${tKey}.desc`)}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1">
        {children ?? <p className="text-sm text-muted-foreground">{t("rep.noParams")}</p>}
      </CardContent>
      <CardFooter className="justify-end gap-2">{footer}</CardFooter>
    </Card>
  );
}

/** A report with no backend yet — disabled "Rapport opvragen" + a "binnenkort" badge. */
function DisabledFooter() {
  const { t } = useI18n();
  return (
    <>
      <Badge variant="secondary">{t("rep.soon")}</Badge>
      <Button size="sm" disabled>
        {t("rep.request")}
      </Button>
    </>
  );
}

const isTrue = (v: unknown) => v === true || v === "true" || v === 1;
const buildCsv = (headers: string[], rows: Array<Array<unknown>>): string =>
  [headers.join(","), ...rows.map((r) => r.map(csvEsc).join(","))].join("\n");

/** Generic "fetch → export" report card: a working button that runs `produce` (which builds + downloads). */
function ExportCard({ tKey, perm, produce, children }: { tKey: string; perm: PermissionCode; produce: () => Promise<void>; children?: React.ReactNode }) {
  const { t } = useI18n();
  const dl = useMutation({ mutationFn: produce });
  return (
    <ReportCard tKey={tKey} footer={
      <Can perm={perm} fallback={<DisabledFooter />}>
        <Button size="sm" onClick={() => dl.mutate()} disabled={dl.isPending}>
          <Download aria-hidden />{dl.isPending ? t("rep.requesting") : t("rep.request")}
        </Button>
      </Can>
    }>
      {children}
      {dl.isError && <p className="text-sm text-destructive">{t("rep.failed")} {errText(dl.error)}</p>}
    </ReportCard>
  );
}

// 1. Complexiteit — verwerkingen met afgeleide complexiteit (verhoogd risico → Hoog).
function ComplexityReport() {
  return <ExportCard tKey="1" perm="verwerking.read" produce={async () => {
    const rows = (await api.listVerwerkingen()) as Array<Record<string, unknown>>;
    downloadBlob(buildCsv(["Naam", "Status", "Verhoogd risico", "Incidenteel", "Complexiteit"],
      rows.map((r) => [r.name, r.status, isTrue(r.heightenedRisk) ? "Ja" : "Nee", isTrue(r.incidental) ? "Ja" : "Nee", isTrue(r.heightenedRisk) ? "Hoog" : "Standaard"])),
      "text/csv", "complexiteit-verwerkingen.csv");
  }} />;
}

// 2/3. Activiteitenplanning — de ingeschakelde regime-activiteiten (optioneel gefilterd op de gebruiker).
function ActivityPlanningReport({ tKey, mine }: { tKey: string; mine: boolean }) {
  const session = useSession();
  return <ExportCard tKey={tKey} perm="verwerking.read" produce={async () => {
    let rows = ((await api.getRegime()) as Array<Record<string, unknown>>).filter((r) => isTrue(r.enabled));
    if (mine) rows = rows.filter((r) => r.assignedUserId === session.user.id);
    downloadBlob(buildCsv(["Code", "Activiteit", "Toegewezen aan"],
      rows.map((r) => [r.activityCode ?? "", r.activityName ?? "", r.assignedUserId ?? "—"])),
      "text/csv", mine ? "mijn-activiteitenplanning.csv" : "activiteitenplanning.csv");
  }} />;
}

// 7. Ticket rapport — alle tickets met status.
function TicketReport() {
  return <ExportCard tKey="7" perm="ticket.read" produce={async () => {
    const rows = (await api.listTickets()) as Array<Record<string, unknown>>;
    downloadBlob(buildCsv(["Titel", "Status"], rows.map((r) => [r.title ?? "", r.status ?? ""])),
      "text/csv", "ticket-rapport.csv");
  }} />;
}

// 8. Privacyteam rapport — de roster (gebruiker × functie).
function PrivacyteamReport() {
  return <ExportCard tKey="8" perm="privacyteam_membership.read" produce={async () => {
    const rows = (await api.listPrivacyteam()) as Array<Record<string, unknown>>;
    downloadBlob(buildCsv(["Naam", "E-mail", "Functie"], rows.map((r) => [r.fullName ?? r.userName ?? "", r.email ?? "", r.functieName ?? ""])),
      "text/csv", "privacyteam-rapport.csv");
  }} />;
}

// 9. Verwerkersrapport — externe partijen met rol 'verwerker'.
function VerwerkersReport() {
  return <ExportCard tKey="9" perm="partner_relationship.read" produce={async () => {
    const rows = ((await api.listExternalParties()) as Array<Record<string, unknown>>).filter((r) => String(r.partyKind) === "processor");
    downloadBlob(buildCsv(["Naam", "Land", "Contactpersoon", "Contact-e-mail"],
      rows.map((r) => [r.name ?? "", r.country ?? "", r.contactName ?? "", r.contactEmail ?? ""])),
      "text/csv", "verwerkersrapport.csv");
  }} />;
}

// 10. Privacyverklaring sjabloon — een conceptverklaring opgebouwd uit het verwerkingsregister.
function PrivacyStatementReport() {
  // Server-generated Art.13/14 notice assembled from the full RoPA (per-doel purposes, data categories,
  // legal bases, recipients, retention + the data-subject-rights section) — domain.ropaToPrivacyStatement.
  // Replaces the earlier client-side stub that only listed names. (P2)
  return <ExportCard tKey="10" perm="verwerking.read" produce={async () => {
    const body = await api.exportRopa("privacy-statement");
    downloadBlob(body, "text/markdown", "privacyverklaring-concept.md");
  }} />;
}

// ── 4. Register van verwerkingen — WIRED to exportRopa (RoPA Art.30 CSV/XML) ──────────────────────
const ROPA_STATUSES = ["all", "new", "active", "inactive"] as const;
const ROPA_FORMATS = ["csv", "xml"] as const;

function RegisterVerwerkingenCard() {
  const { t } = useI18n();
  const [format, setFormat] = useState<(typeof ROPA_FORMATS)[number]>("csv");
  // status is a captured legacy parameter (e15); the RoPA export endpoint takes format only, so the
  // status filter is presented but not yet sent (frontend-only; no endpoint change).
  const [status, setStatus] = useState<(typeof ROPA_STATUSES)[number]>("all");

  const dl = useMutation({
    mutationFn: async () => {
      const text = await api.exportRopa(format);
      downloadBlob(text, format === "xml" ? "application/xml" : "text/csv", `register-verwerkingen-art30.${format}`);
    },
  });

  return (
    <ReportCard
      tKey="4"
      footer={
        <Can perm="verwerking.read" fallback={<DisabledFooter />}>
          <Button size="sm" onClick={() => dl.mutate()} disabled={dl.isPending}>
            <Download aria-hidden />
            {dl.isPending ? t("rep.requesting") : t("rep.request")}
          </Button>
        </Can>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="ropa-format">{t("rep.format")}</Label>
          <Select value={format} onValueChange={(v) => setFormat(v as (typeof ROPA_FORMATS)[number])}>
            <SelectTrigger id="ropa-format" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="xml">XML</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ropa-status">{t("rep.status")}</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as (typeof ROPA_STATUSES)[number])}>
            <SelectTrigger id="ropa-status" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROPA_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`rep.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {dl.isError && <p className="text-sm text-destructive">{t("rep.failed")} {errText(dl.error)}</p>}
      </div>
    </ReportCard>
  );
}

// ── 5. Register van bestandssystemen — WIRED to listBestandssystemen (client-side CSV) ────────────
const csvEsc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;

function RegisterBestandssystemenCard() {
  const { t } = useI18n();
  const dl = useMutation({
    mutationFn: async () => {
      const rows = (await api.listBestandssystemen()) as Array<Record<string, unknown>>;
      const cols = ["name", "category", "description"] as const;
      const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => csvEsc(r[c])).join(","))].join("\n");
      downloadBlob(csv, "text/csv", "register-bestandssystemen.csv");
    },
  });
  return (
    <ReportCard
      tKey="5"
      footer={
        <Can perm="bestandssysteem.read" fallback={<DisabledFooter />}>
          <Button size="sm" onClick={() => dl.mutate()} disabled={dl.isPending}>
            <Download aria-hidden />
            {dl.isPending ? t("rep.requesting") : t("rep.request")}
          </Button>
        </Can>
      }
    >
      <p className="text-sm text-muted-foreground">{t("rep.5.note")}</p>
      {dl.isError && <p className="text-sm text-destructive">{t("rep.failed")} {errText(dl.error)}</p>}
    </ReportCard>
  );
}

// ── 6. AVG-verantwoordingsstatus — WIRED to the computed accountability overview (W27) ─────────────
interface AccountabilityCsv {
  pct: number; inOrde: number; totalEnabled: number;
  sections?: Array<{ sectionCode?: string | null; name?: string | null; total: number; inOrde: number; pct: number }>;
}

function VerantwoordingsstatusCard() {
  const { t } = useI18n();
  const dl = useMutation({
    mutationFn: async () => {
      const o = (await api.getAccountabilityOverview()) as AccountabilityCsv;
      const lines = [
        ["sectie", "totaal", "in_orde", "pct"].join(","),
        ...(o.sections ?? []).map((s) => [csvEsc(s.name ?? s.sectionCode), s.total, s.inOrde, s.pct].join(",")),
        ["TOTAAL", o.totalEnabled, o.inOrde, o.pct].join(","),
      ];
      downloadBlob(lines.join("\n"), "text/csv", "avg-verantwoordingsstatus.csv");
    },
  });
  return (
    <ReportCard
      tKey="6"
      footer={
        <Can perm="verwerking.read" fallback={<DisabledFooter />}>
          <Button size="sm" onClick={() => dl.mutate()} disabled={dl.isPending}>
            <Download aria-hidden />
            {dl.isPending ? t("rep.requesting") : t("rep.request")}
          </Button>
        </Can>
      }
    >
      <div className="flex flex-col gap-3">
        <PeriodFields disabled />
        <p className="text-xs text-muted-foreground">{t("rep.6.note")}</p>
        {dl.isError && <p className="text-sm text-destructive">{t("rep.failed")} {errText(dl.error)}</p>}
      </div>
    </ReportCard>
  );
}

// ── period param (begin/end month) — used by 6 (AVG-verantwoordingsstatus) ────────────────────────
function PeriodFields({ disabled }: { disabled?: boolean }) {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="grid gap-1.5">
        <Label htmlFor="period-begin">{t("rep.periodBegin")}</Label>
        <Input id="period-begin" type="month" disabled={disabled} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="period-end">{t("rep.periodEnd")}</Label>
        <Input id="period-end" type="month" disabled={disabled} />
      </div>
    </div>
  );
}

